import { useState } from 'react';
import { Search, Shield, FileCheck, ExternalLink, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, HelpCircle, XCircle, Activity } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { usePayerPolicies, type PayerPolicy } from '@/hooks/usePayerPolicies';
import { cn } from '@/lib/utils';

const PA_STATUS_CONFIG = {
  Yes: { color: 'bg-destructive/10 text-destructive border-destructive/30', icon: XCircle, label: 'PA Required', dot: 'bg-destructive' },
  No: { color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30', icon: CheckCircle2, label: 'No PA Required', dot: 'bg-emerald-500' },
  Conditional: { color: 'bg-amber-500/10 text-amber-700 border-amber-500/30', icon: AlertCircle, label: 'Conditional', dot: 'bg-amber-500' },
  Unknown: { color: 'bg-muted text-muted-foreground border-border', icon: HelpCircle, label: 'Unknown', dot: 'bg-muted-foreground' },
};

function DocumentationChecklist({ requirements }: { requirements: Record<string, string | boolean> }) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const entries = Object.entries(requirements).filter(([, v]) => v);

  if (entries.length === 0) return <p className="text-sm text-muted-foreground italic">No specific documentation requirements listed.</p>;

  const labels: Record<string, string> = {
    clinical_notes: 'Clinical Notes',
    imaging: 'Imaging',
    failed_therapy: 'Failed Conservative Therapy',
    functional_scores: 'Functional Scores',
    labs: 'Labs / Pre-op',
    clearances: 'Medical Clearances',
    neurology: 'Neurological Assessment',
    injury_documentation: 'Injury Documentation',
  };

  const total = entries.length;
  const done = Object.values(checked).filter(Boolean).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-primary" />
          Clinical Prep Checklist
        </h4>
        <Badge variant="outline" className="text-xs">
          {done}/{total} complete
        </Badge>
      </div>
      <div className="space-y-2">
        {entries.map(([key, value]) => {
          const label = labels[key] || key;
          const detail = typeof value === 'string' ? value : null;
          return (
            <label
              key={key}
              className={cn(
                'flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-all',
                checked[key]
                  ? 'bg-emerald-500/5 border-emerald-500/30'
                  : 'bg-card border-border hover:border-primary/30'
              )}
            >
              <Checkbox
                checked={!!checked[key]}
                onCheckedChange={(v) => setChecked(prev => ({ ...prev, [key]: !!v }))}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <span className={cn('text-sm font-medium', checked[key] && 'line-through text-muted-foreground')}>
                  {label}
                </span>
                {detail && (
                  <p className={cn('text-xs mt-0.5', checked[key] ? 'text-muted-foreground/60' : 'text-muted-foreground')}>
                    {detail}
                  </p>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function PolicyCard({ policy, isExpanded, onToggle }: { policy: PayerPolicy; isExpanded: boolean; onToggle: () => void }) {
  const status = PA_STATUS_CONFIG[policy.pa_required];
  const StatusIcon = status.icon;
  const channels = Array.isArray(policy.submission_channels) ? policy.submission_channels : [];

  return (
    <Card className={cn('transition-all', isExpanded && 'ring-1 ring-primary/20')}>
      <button onClick={onToggle} className="w-full text-left">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className={cn('w-2.5 h-2.5 rounded-full shrink-0', status.dot)} />
              <div className="min-w-0">
                <CardTitle className="text-base truncate">{policy.payer_name}</CardTitle>
                <CardDescription className="text-xs mt-0.5">{policy.payer_type} • Confidence: {policy.confidence}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={cn('text-xs border', status.color)}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
              {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>
        </CardHeader>
      </button>

      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          <Separator />
          <div>
            <p className="text-sm font-medium">{policy.cpt_code} — {policy.cpt_description}</p>
            {policy.pa_rules_summary && (
              <p className="text-sm text-muted-foreground mt-1">{policy.pa_rules_summary}</p>
            )}
          </div>
          <DocumentationChecklist requirements={policy.documentation_requirements} />
          {channels.length > 0 && channels[0]?.channel !== 'N/A' && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Submission Channels</h4>
              <div className="flex flex-wrap gap-2">
                {channels.map((ch, i) => (
                  ch.link ? (
                    <Button key={i} variant="outline" size="sm" asChild>
                      <a href={ch.link} target="_blank" rel="noopener noreferrer" className="gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5" />
                        {ch.channel}
                      </a>
                    </Button>
                  ) : (
                    <Badge key={i} variant="secondary">{ch.channel}</Badge>
                  )
                ))}
              </div>
            </div>
          )}
          {policy.source_url && (
            <Button variant="link" size="sm" asChild className="p-0 h-auto">
              <a href={policy.source_url} target="_blank" rel="noopener noreferrer" className="text-xs gap-1">
                <ExternalLink className="w-3 h-3" />
                View Policy Source
              </a>
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function BatchView({ policies }: { policies: PayerPolicy[] }) {
  const payers = [...new Set(policies.map(p => p.payer_name))];
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Top Ohio Payers — MSK PA Overview
        </CardTitle>
        <CardDescription>Quick reference for prior authorization requirements across major Ohio payers</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="pr-4">Payer</th>
                <th className="px-2">Type</th>
                <th className="px-2 text-center">27447</th>
                <th className="px-2 text-center">27130</th>
                <th className="px-2 text-center">22551</th>
                <th className="px-2 text-center">72148</th>
              </tr>
            </thead>
            <tbody>
              {payers.map(payer => {
                const payerPolicies = policies.filter(p => p.payer_name === payer);
                const type = payerPolicies[0]?.payer_type || '';
                const getStatus = (cpt: string) => {
                  const pol = payerPolicies.find(p => p.cpt_code === cpt);
                  if (!pol) return null;
                  const cfg = PA_STATUS_CONFIG[pol.pa_required];
                  return <div className={cn('w-3 h-3 rounded-full mx-auto', cfg.dot)} title={cfg.label} />;
                };
                return (
                  <tr key={payer}>
                    <td className="pr-4 font-medium">{payer}</td>
                    <td className="px-2 text-muted-foreground text-xs">{type}</td>
                    <td className="px-2 text-center">{getStatus('27447')}</td>
                    <td className="px-2 text-center">{getStatus('27130')}</td>
                    <td className="px-2 text-center">{getStatus('22551')}</td>
                    <td className="px-2 text-center">{getStatus('72148')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-destructive" /> PA Required</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> No PA</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Conditional</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PANavigator() {
  const { policies, allPolicies, loading, searchQuery, setSearchQuery, showNextGen, setShowNextGen } = usePayerPolicies();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [view, setView] = useState<'search' | 'batch'>('search');

  return (
    <div className="pt-24 pb-20">
      <div className="section-container max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
            <Shield className="w-3.5 h-3.5" />
            LINK Medical
          </div>
          <h1 className="heading-2">Ohio PA Navigator</h1>
          <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
            Look up prior authorization requirements, clinical documentation checklists, and submission channels for Ohio payers by CPT code.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Search by CPT code (e.g. "27447") or payer name...'
            className="pl-12 h-12 text-base rounded-xl border-2 focus-visible:ring-primary/30"
          />
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex gap-2">
            <Button variant={view === 'search' ? 'default' : 'outline'} size="sm" onClick={() => setView('search')}>
              Search Results
            </Button>
            <Button variant={view === 'batch' ? 'default' : 'outline'} size="sm" onClick={() => setView('batch')}>
              Batch View
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={showNextGen} onCheckedChange={setShowNextGen} id="nextgen" />
            <label htmlFor="nextgen" className="text-sm text-muted-foreground cursor-pointer">
              Next Gen Medicaid 2026
            </label>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading policies...</div>
        ) : view === 'batch' ? (
          <BatchView policies={allPolicies} />
        ) : policies.length === 0 ? (
          <div className="text-center py-12">
            <HelpCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No results found. Try a different CPT code or payer name.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{policies.length} result{policies.length !== 1 ? 's' : ''}</p>
            {policies.map(p => (
              <PolicyCard
                key={p.id}
                policy={p}
                isExpanded={expandedId === p.id}
                onToggle={() => setExpandedId(prev => prev === p.id ? null : p.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
